import {
  getAvailabilityState,
  getSelectedAvailabilityBakeryId,
  getSelectedPickupDay,
  isInventoryWindowActive,
  parseMf16Ids,
  parseMf20Stock,
  parseMf34PreorderAllowed,
  parseMf36StockSynced,
} from '@theme/availability';
import { ThemeEvents } from '@theme/events';

/**
 * Recommendations carousel — PERMANENT MF16 filter + SSD-driven MF20 honoring.
 *
 * PERMANENT MF16 (always filter by SSD bakery):
 *   The Liquid blocks/sections (blocks/product-recommendations.liquid +
 *   sections/product-recommendations.liquid) skip any recommended product
 *   whose `mf16_available_locations` doesn't include the active bakery id —
 *   but only when that id reaches Liquid. The id comes from
 *   `request.parameters.bakery_id` (set on the Section Rendering API fetch
 *   URL below) OR `customer.metafields.custom.mf10_customer_store` (server-
 *   side fallback for logged-in customers). The Liquid stamps the bakery id
 *   it actually used onto `data-rec-hydrated-bakery-id` on the
 *   `<product-recommendations>` root. `#loadRecommendations` below compares
 *   that stamp against the live SSD bakery id and force-re-fetches whenever
 *   they disagree — even when Shopify pre-populated the section so
 *   `recommendations.performed === 'true'`. That's what makes the MF16
 *   filter "permanent" instead of only applying on the JS-driven fetch path.
 *
 * SSD-DRIVEN MF20 (honor the `pickupDay='tomorrow'` bypass like everywhere else):
 *   When the shopper has selected `pickupDay='tomorrow'` in the SSD, MF20
 *   stock-today is irrelevant — tomorrow's bake hasn't happened yet, so a
 *   zero-today SKU may very well be available for tomorrow's pickup. The
 *   recommendations carousel honors this exactly like PLP / PDP / cart:
 *     1. The fetch URL passes the LIVE SSD pickup day (`&pickup_day=today|tomorrow`),
 *        so the Liquid `availability-state.liquid` chain computes badges
 *        server-side with the same tomorrow-bypass logic. No FOUC.
 *     2. `<product-recommendations>` carries `data-rec-hydrated-pickup-day`
 *        stamped to the value used by the server render. `#loadRecommendations`
 *        below force-re-fetches whenever the live SSD pickup day disagrees
 *        with that stamp — analogous to the bakery-id mismatch gate.
 *     3. We deliberately do NOT stamp `data-availability-pickup-day-override`
 *        anymore. `availability.js` `resolveEffectivePickupDay` falls back
 *        to the global SSD value, which matches the just-fetched HTML.
 */

class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations, keyed by fetch URL. The URL includes both
   * the current `bakery_id` AND the live SSD `pickup_day`, so switching
   * either busts the cache and triggers a re-fetch via `#onStoreChanged`.
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    // Re-fetch when the selected store changes so availability badges in the
    // recommendation cards reflect the new bakery without a full page reload.
    const stateApi = /** @type {any} */ (window).StoreSelectionState;

    if (stateApi && typeof stateApi.subscribe === 'function') {
      stateApi.subscribe(() => this.#onStoreChanged());
    } else {
      window.addEventListener('store-selection:changed', () => this.#onStoreChanged());
      window.addEventListener('store-selection:cleared', () => this.#onStoreChanged());
    }
  }

  /**
   * MF01 internal bakery id for server-side availability (`bakery_id` query param).
   *
   * @returns {string | null}
   */
  #getSelectedBakeryId() {
    return getSelectedAvailabilityBakeryId();
  }

  /**
   * Live SSD pickup day (`'today'` | `'tomorrow'`) — fed straight into the
   * fetch URL's `pickup_day` param and into the pruning logic below so the
   * carousel reflects the same MF20 tomorrow-bypass behaviour as PLP / PDP /
   * cart instead of being permanently strict-today.
   *
   * @returns {'today' | 'tomorrow'}
   */
  #getSelectedPickupDay() {
    return getSelectedPickupDay();
  }

  /**
   * Called on store-selection:changed / store-selection:cleared.
   * Clears the recommendation cache and re-fetches with the updated bakery_id.
   *
   * Note: we DON'T touch `data-recommendations-performed` here anymore.
   * `#loadRecommendations` gates on bakery-id mismatch (current SSD vs
   * `data-rec-hydrated-bakery-id`), so the bakery change naturally invalidates
   * the previous render without needing the `performed` flag as a tripwire.
   */
  #onStoreChanged() {
    this.#cachedRecommendations = {};
    this.#loadRecommendations();
  }

  /**
   * Load the product recommendations.
   *
   * Gating logic (PERMANENT MF16 FILTER + SSD-DRIVEN MF20):
   *   We used to early-return when `data-recommendations-performed === 'true'`,
   *   which silently skipped the JS re-fetch whenever Shopify had pre-populated
   *   the section server-side — that left the MF16 filter unapplied (the Liquid
   *   loop only filters when `request.parameters.bakery_id` is present, which
   *   only happens during a JS-driven Section Rendering API fetch). The result
   *   was an unfiltered carousel showing products not stocked at the shopper's
   *   bakery.
   *
   *   The current gate is TWO-AXIS mismatch (bakery-id AND pickup-day): skip
   *   the fetch ONLY when BOTH match what the server already rendered for
   *   (stamped on `data-rec-hydrated-bakery-id` + `data-rec-hydrated-pickup-day`
   *   by Liquid). In every other case we MUST re-fetch so the server-side
   *   filter applies with the correct bakery id AND the MF20 tomorrow-bypass
   *   reflects the live SSD pickup day.
   *
   *   Concrete scenarios:
   *     - logged-in customer with mf10 + SSD today: server renders filtered with today, JS sees match on both axes, skips fetch ✓
   *     - same customer but SSD is tomorrow: server renders with today (initial), JS sees pickup-day mismatch, re-fetches with tomorrow ✓
   *     - guest with SSD bakery set: server renders unfiltered (hydrated=''), JS sees bakery mismatch, re-fetches with the SSD bakery id AND pickup day ✓
   *     - guest with no SSD bakery: server renders unfiltered (hydrated=''), JS sees match on both axes, skips fetch ✓
   *     - shopper changes bakery OR pickup day in SSD: `#onStoreChanged` clears cache + calls this, JS sees mismatch, re-fetches ✓
   *     - shopper navigates to a new PDP via instant-nav: server renders with the
   *       new product but the same bakery + pickup-day context, JS sees match on both axes, skips fetch ✓
   */
  #loadRecommendations() {
    const { productId, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (!this.dataset.url || this.dataset.url === 'null' || this.dataset.url.trim() === '') {
      return;
    }

    const currentBakeryId = this.#getSelectedBakeryId() || '';
    const hydratedBakeryId = this.dataset.recHydratedBakeryId || '';
    const currentPickupDay = this.#getSelectedPickupDay();
    // Default the stamp to 'today' (matches Liquid's behaviour when
    // `request.parameters.pickup_day` is blank on the initial page render).
    const hydratedPickupDay = this.dataset.recHydratedPickupDay || 'today';

    if (
      currentBakeryId === hydratedBakeryId &&
      currentPickupDay === hydratedPickupDay &&
      this.dataset.recommendationsPerformed === 'true'
    ) {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          // Prune MF16-miss cards from the off-DOM fragment BEFORE injecting,
          // so the `<slideshow-component>` upgrades with the correct slide
          // count AND the `.slideshow-controls__dots` row matches the visible
          // slides. Doing this on the fragment (not the live DOM) is much
          // simpler than tearing down a freshly-initialized slideshow.
          //
          // Pass the live SSD pickup day so the MF16/MF20 state calc inside
          // the prune mirrors the server-side `availability-state.liquid`
          // render — when `pickupDay='tomorrow'`, MF20 is bypassed and only
          // MF16 misses are pruned. When `pickupDay='today'`, the existing
          // strict MF20 path still applies.
          ProductRecommendations.#pruneUnavailableInFragment(
            recommendations,
            currentBakeryId,
            currentPickupDay
          );

          this.dataset.recommendationsPerformed = 'true';
          this.dataset.recHydratedBakeryId = currentBakeryId;
          this.dataset.recHydratedPickupDay = currentPickupDay;
          this.innerHTML = recommendations.innerHTML;
          document.dispatchEvent(
            new CustomEvent(ThemeEvents.PageContentAppended, { bubbles: true })
          );
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and caches the result for future use.
   * Appends `bakery_id` (current SSD bakery) and `pickup_day` (live SSD
   * value) so the Liquid section computes availability server-side for each
   * product card with the same tomorrow-bypass behaviour as PLP / PDP / cart.
   *
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const bakeryId = this.#getSelectedBakeryId();
    const pickupDay = this.#getSelectedPickupDay();
    let url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    if (bakeryId) {
      url += `&bakery_id=${encodeURIComponent(bakeryId)}`;
    }
    // Pass the LIVE SSD pickup day so the server-side
    // `availability-state.liquid` honours the tomorrow-bypass when relevant.
    // When the shopper hasn't committed to a bakery, the value still defaults
    // to 'today' from `getSelectedPickupDay()` — which is correct, since with
    // no bakery selected there's nothing for MF20 to gate against anyway.
    url += `&pickup_day=${encodeURIComponent(pickupDay)}`;
    // MF36 enforcement window (MO08 local bakery time bounds) so the
    // server-rendered badges apply the same inventory-synced gate as the
    // client. Accepted simplification: a mid-session window flip doesn't force
    // a re-fetch — the client hydration pass corrects badges.
    url += `&inventory_window=${isInventoryWindowActive() ? 'true' : 'false'}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } catch {
      return { success: false, status: 0 };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }

  /**
   * Walk the off-DOM fragment for MF16-miss cards (state === 'unavailable')
   * and remove their wrapping `<slideshow-slide>` / `.resource-list__item` AND
   * a matching number of trailing dots from `.slideshow-controls__dots` so the
   * slideshow's pagination matches the visible slide count once the fragment
   * is injected.
   *
   * Why prune on the fragment (not the live DOM):
   *   `<slideshow-component>` runs `connectedCallback` synchronously when the
   *   fragment hits the live DOM, caches `slidesPerView`, computes the
   *   `--slideshow-timeline` scope, and wires up scroll/intersection
   *   observers. Removing slides AFTER that means we'd have to surgically
   *   re-trigger every one of those steps. Removing them BEFORE means the
   *   slideshow upgrades with the correct count on first paint, no teardown
   *   needed.
   *
   * Why trailing dots specifically:
   *   The dots' click handlers are `on:click="/select/<index>"` where `<index>`
   *   is the DOM position in `refs.slides`. After we remove slides from the
   *   middle of the list, the remaining slides shift up, so the dot at DOM
   *   position N (which now navigates to the Nth REMAINING slide) is exactly
   *   what we want. Dropping the LAST few dots (one per removed slide) gives
   *   us the correct dot count, and every remaining dot still points at the
   *   right slide via its `/select/N` handler resolving against the shorter
   *   `refs.slides` array. This avoids re-mapping click handlers entirely.
   *
   * Why only `'unavailable'` is pruned (not `'sold_out'`):
   *   Sold-out cards (MF16 hit, MF20 ≤ 0 with today pickup) are still
   *   interesting to the shopper — they see the product exists at their
   *   bakery but won't be sellable until restock. Pruning them would erase
   *   that signal entirely. So we only prune the harder MF16-miss state
   *   (product isn't carried at this bakery at all). When pickup day is
   *   tomorrow, MF20 is bypassed and there's no `'sold_out'` state to worry
   *   about — every MF16-hit becomes `'available'`.
   *
   * @param {Element} fragment - The off-DOM `<product-recommendations>` element parsed from the fetch response
   * @param {string} bakeryId - The current SSD bakery id (empty string for guest with no selection)
   * @param {'today' | 'tomorrow'} pickupDay - Live SSD pickup day (forwarded
   *   to `getAvailabilityState` so the tomorrow-bypass is honored — matches
   *   the server-side `availability-state.liquid` render)
   */
  static #pruneUnavailableInFragment(fragment, bakeryId, pickupDay) {
    if (!bakeryId) return;

    const stateEls = fragment.querySelectorAll(
      '[data-availability-state]:not([data-availability-suppress-badge="true"])'
    );
    if (stateEls.length === 0) return;

    /** @type {Set<Element>} */
    const slidesToRemove = new Set();

    for (const stateEl of stateEls) {
      const el = /** @type {HTMLElement} */ (stateEl);
      const mf16Ids = parseMf16Ids(el.dataset.availabilityMf16 || '');
      const mf20Stock = parseMf20Stock(el.dataset.availabilityMf20 || '{}');
      // MF34 threaded so preorder-blocked cards (MF34 false + Tomorrow pickup,
      // 'unavailable') are pruned like MF16 misses — without this the
      // defence-in-depth CSS in availability-state.liquid hides the slide
      // post-hydration but leaves ghost dots in the slideshow controls.
      const preorderAllowed = parseMf34PreorderAllowed(el.dataset.availabilityMf34);
      // MF36 threaded for lock-step with the shared state computation — it only
      // ever moves a card between available and sold_out, and neither is pruned,
      // but keeping the inputs identical avoids surprises if the prune rules
      // ever change.
      const stockSynced = parseMf36StockSynced(el.dataset.availabilityMf36);
      const { state } = getAvailabilityState(
        mf16Ids,
        mf20Stock,
        bakeryId,
        pickupDay,
        preorderAllowed,
        stockSynced
      );

      if (state !== 'unavailable') continue;

      // Prefer the outer `<slideshow-slide>` wrapper when present (carousel
      // layout) — `.resource-list__item` lives INSIDE it, and removing only
      // the inner div would leave an empty slot in the carousel. Fall back
      // to `.resource-list__item` for grid layouts (no slideshow-slide).
      const wrapper =
        el.closest('slideshow-slide') ?? el.closest('.resource-list__item');
      if (wrapper) slidesToRemove.add(wrapper);
    }

    if (slidesToRemove.size === 0) return;

    for (const wrapper of slidesToRemove) {
      wrapper.remove();
    }

    const dotsContainer = fragment.querySelector('.slideshow-controls__dots');
    if (dotsContainer) {
      for (let i = 0; i < slidesToRemove.size; i++) {
        const last = dotsContainer.lastElementChild;
        if (!last) break;
        last.remove();
      }
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
